import { patch, post } from 'common/apiClient';
import { useIsLoading } from 'common/hooks';
import { useMemo } from 'react';

import { createValidation } from './common';
import { UserPayload } from './profile';

/**
 * Types
 */

export type User = {
  id: string;
  email: string;
  isConfirmed: boolean;
  Username: string;
};

export type CreateUserPayload = {
  email: string;
  password: string;
  Username: string;
};

export type ChangePasswordPayload = {
  Username: string | undefined;
  password: string | undefined;
  currentPassword: string | undefined;
  confirmPassword?: string | undefined; // Add optional property
};

export type PasswordResponse = {
  data: UserPayload;
};

export type UserResponse = {
  user: User;
};

export type ConfirmEmailResponse = boolean;

export type ForgottenPasswordPayload = {
  email: string;
};

export type ForgottenPasswordResponse = boolean;

export type ResetPasswordPayload = {
  password: string;
};

export type ResetPasswordResponse = boolean;

/**
 * Constants
 */

const PASSWORD_MIN_LENGTH = 6;

/**
 * Validations
 */

//export const emailRule = { required: true, type: 'email' } as const;
export const emailRule = { required: true, type: 'string' } as const;

export const passwordRule = { required: true, type: 'string', min: PASSWORD_MIN_LENGTH } as const;

export const useCreateUserValidation = () =>
  useMemo(
    () =>
      createValidation<CreateUserPayload>({
        email: [emailRule],
        password: [passwordRule],
        Username: [emailRule],
      }),
    []
  );

export const useForgottenPasswordValidation = () =>
  useMemo(
    () =>
      createValidation<ForgottenPasswordPayload>({
        email: [emailRule],
      }),
    []
  );

export const useChangePasswordValidation = () =>
  useMemo(
    () =>
      createValidation<ChangePasswordPayload>({
        currentPassword: [{ required: true, type: 'string' }],
        password: [passwordRule],
        Username: [emailRule],
        confirmPassword: [
          { required: true, message: 'Please confirm your new password!' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The two passwords do not match!'));
            },
          }),
        ],
      }),
    []
  );

export const useResetPasswordValidation = () =>
  useMemo(
    () =>
      createValidation<ResetPasswordPayload>({
        password: [passwordRule],
      }),
    []
  );

/**
 * Endpoints
 */

export const useCreateUser = () => {
  const { isLoading, startLoading, stopLoading } = useIsLoading('user.createUser');

  return useMemo(
    () => ({
      isLoading,
      createUser: async (payload: CreateUserPayload) => {
        try {
          await startLoading();
          return await post<UserResponse>('/user', payload);
        } finally {
          await stopLoading();
        }
      },
    }),
    [isLoading, startLoading, stopLoading]
  );
};

export const useConfirmEmail = (token: string) => {
  const { isLoading, startLoading, stopLoading } = useIsLoading('user.confirmEmail');

  return useMemo(
    () => ({
      isLoading,
      confirmEmail: async () => {
        try {
          await startLoading();
          return await patch<ConfirmEmailResponse>(`/user/confirm-email/${token}`);
        } finally {
          await stopLoading();
        }
      },
    }),
    [isLoading, startLoading, stopLoading, token]
  );
};

export const useFetchUser = (userId: string) => userId;

export const useChangePassword = () => {
  const { isLoading, startLoading, stopLoading } = useIsLoading('user.changePassword');

  return useMemo(
    () => ({
      isLoading,
      changePassword: async (payload: UserPayload) => {
        try {
          await startLoading();
          return await patch<PasswordResponse>('/usermanagement/changePassword', payload);
        } finally {
          await stopLoading();
        }
      },
    }),
    [isLoading, startLoading, stopLoading]
  );
};

export const useForgottenPassword = () => {
  const { isLoading, startLoading, stopLoading } = useIsLoading('user.forgottenPassword');

  return useMemo(
    () => ({
      isLoading,
      forgottenPassword: async (payload: ForgottenPasswordPayload) => {
        try {
          await startLoading();
          return await post<ForgottenPasswordResponse>('/user/forgot-password', payload);
        } finally {
          await stopLoading();
        }
      },
    }),
    [isLoading, startLoading, stopLoading]
  );
};

export const useResetPassword = (token: string) => {
  const { isLoading, startLoading, stopLoading } = useIsLoading('user.resetPassword');

  return useMemo(
    () => ({
      isLoading,
      resetPassword: async (payload: ResetPasswordPayload) => {
        try {
          await startLoading();
          return await patch<ResetPasswordResponse>('/user/reset-password', { ...payload, token });
        } finally {
          await stopLoading();
        }
      },
    }),
    [isLoading, startLoading, stopLoading, token]
  );
};

export const changepassword = (payload?: UserPayload) =>
  post<PasswordResponse>('/usermanagement/changePassword', payload);
